import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';
import config from '@config';

// https://www.gatsbyjs.com/docs/add-seo-component/

const Head = ({ title, description, image }) => {
  const { pathname } = useLocation();

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            defaultTitle: title
            defaultDescription: description
            siteUrl
            defaultImage: image
            twitterUsername
          }
        }
      }
    `,
  );

  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
  } = site.siteMetadata;

  const seoImage = image || defaultImage;
  const imageUrl = seoImage && seoImage.startsWith('http') ? seoImage : `${siteUrl}${seoImage}`;
  const canonicalPath = pathname === '/' ? '/' : pathname.replace(/\/?$/, '/');

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: imageUrl,
    url: `${siteUrl}${canonicalPath}`,
  };

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      name: seo.title,
      url: seo.url,
      mainEntity: {
        '@type': 'Person',
        name: 'Gil Henkin',
        url: siteUrl,
        email: `mailto:${config.email}`,
        jobTitle: 'Low-Latency Quantitative Engineer',
        sameAs: config.socialMedia.map(({ url }) => url),
        knowsAbout: [
          'Low-latency systems',
          'High-frequency trading infrastructure',
          'Real-time backend systems',
          'Ethereum PBS and MEV',
          'Crypto protocol design',
          'dApp architecture',
          'Rust',
          'C/C++',
          'Python',
          'TypeScript',
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: defaultTitle,
      url: siteUrl,
      description: defaultDescription,
    },
  ];

  return (
    <Helmet title={seo.title}>
      <html lang="en" />

      <link rel="canonical" href={seo.url} />
      <link rel="alternate" type="text/plain" href={`${siteUrl}/llms.txt`} title="llms.txt" />

      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />

      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      {twitterUsername && <meta name="twitter:creator" content={twitterUsername} />}
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      {structuredData.map(data => (
        <script type="application/ld+json" key={data['@type']}>
          {JSON.stringify(data)}
        </script>
      ))}

      <meta name="google-site-verification" content="DCl7VAf9tcz6eD9gb67NfkNnJ1PKRNcg8qQiwpbx9Lk" />
    </Helmet>
  );
};

export default Head;

Head.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
};

Head.defaultProps = {
  title: null,
  description: null,
  image: null,
};
